import apiFetch from '@wordpress/api-fetch';
import type { APIFetchMiddleware } from '@wordpress/api-fetch/build-types/types';
import type { APIEndpoints } from '../typings/api';

interface CacheEntry {
	promise: Promise<any>;
	timestamp: number;
}

const CACHE_EXPIRY = 30 * 1000; // 30 seconds
const cache = new Map<string, CacheEntry>();
const currentPromises = new Map<string, Promise<any>>();

/**
 * Creates a cache key from the request options
 */
function createCacheKey(options: Record<string, any>): string {
	const { path, url = '', method = 'GET', ...rest } = options;
	const requestUrl = url || path || '';

	const keyParts = {
		url: requestUrl,
		method,
		...(rest.data && { data: rest.data }),
	};

	return JSON.stringify(keyParts);
}

/**
 * Creates a middleware that caches responses when X-Enable-Fetch-Cache header is present.
 * Supports both GET and POST requests when the cache header is provided.
 */
function createCacheMiddleware(): APIFetchMiddleware {
	return (options, next) => {
		const headers = options.headers || {};

		// Only cache requests with the cache header
		if (!headers['X-Enable-Fetch-Cache']) {
			return next(options);
		}

		// Remove the cache header before making the request
		const { 'X-Enable-Fetch-Cache': _, ...remainingHeaders } = headers;
		const optionsWithoutCacheHeader = {
			...options,
			headers: remainingHeaders,
		};

		const cacheKey = createCacheKey(options);

		// Check if there's an existing promise for this request
		const inFlightPromise = currentPromises.get(cacheKey);
		if (inFlightPromise) {
			return inFlightPromise;
		}

		// Check cache
		const cached = cache.get(cacheKey);
		if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
			return cached.promise;
		}

		// Make the request and cache the promise. A rejected promise left in
		// the in-flight map would be replayed for every later identical
		// request, so it has to be released on failure as well as on success.
		const promise = next(optionsWithoutCacheHeader).then(
			(response) => {
				// Store in cache
				cache.set(cacheKey, {
					promise: Promise.resolve(response),
					timestamp: Date.now(),
				});
				// Remove from in-flight promises
				currentPromises.delete(cacheKey);
				return response;
			},
			(error) => {
				// Remove from in-flight promises
				currentPromises.delete(cacheKey);
				throw error;
			}
		);

		// Store the in-flight promise
		currentPromises.set(cacheKey, promise);
		return promise;
	};
}

/**
 * Invalidates all cached responses.
 */
export function invalidateCache() {
	cache.clear();
	// In-flight requests are deduplicated against, so they have to go too.
	currentPromises.clear();
}

/**
 * Invalidates cached responses that match the given URL pattern.
 */
export function invalidateCacheByPattern(pattern: string | RegExp) {
	const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;

	const maps: Array<Map<string, unknown>> = [cache, currentPromises];

	for (const map of maps) {
		for (const [key] of Array.from(map.entries())) {
			try {
				const { url } = JSON.parse(key);
				if (regex.test(url)) {
					map.delete(key);
				}
			} catch (e) {
				window.console.error('Failed to parse cache key:', e);
			}
		}
	}
}

/**
 * A strongly-typed wrapper around WordPress's apiFetch utility.
 *
 * This wrapper provides type safety and autocompletion for API endpoints defined in the APIEndpoints type.
 * It handles both path parameters and query parameters automatically, and provides type checking for request
 * bodies and responses.
 *
 * @example Basic GET request
 * ```typescript
 * // Get all services - TypeScript knows the response type is Service[]
 * const services = await api.get('/gp-bookings/v1/services');
 *
 * // Get a specific service - Path parameter is automatically handled
 * const service = await api.get('/gp-bookings/v1/services/:id', {
 *   params: { id: 123 }
 * });
 * ```
 *
 * @example GET request with query parameters
 * ```typescript
 * // Query params are automatically added to the URL
 * const availability = await api.get('/gp-bookings/v1/availability/days', {
 *   params: {
 *     serviceId: 123,
 *     month: 6,
 *     year: 2025,
 *     resources: [1, 2, 3] // Arrays are properly handled
 *   }
 * });
 * ```
 *
 * @example POST request with body and params
 * ```typescript
 * // Create availability block - Body and params are type checked
 * const block = await api.post('/gp-bookings/v1/admin/availability/blocks', {
 *   data: {
 *     objectId: 123,
 *     objectType: 'service',
 *     startDate: '2025-06-04',
 *     endDate: '2025-06-05'
 *   },
 *   params: { id: 123 } // Optional path/query params
 * });
 * ```
 *
 * @example Using the options parameter to override apiFetch options
 * ```typescript
 * // Override apiFetch options
 * const result = await api.post('/gp-bookings/v1/admin/seed', {
 *   data: { seedType: 'demo' },
 *   options: {
 *     credentials: 'include',
 *     headers: { 'X-Custom-Header': 'value' }
 *   }
 * });
 * ```
 *
 * @example PUT request with path parameter
 * ```typescript
 * // Update availability block - Path param and body are type checked
 * const updated = await api.put('/gp-bookings/v1/admin/availability/blocks/:id', {
 *   params: { id: 123 },
 *   data: {
 *     startDate: '2025-06-04',
 *     endDate: '2025-06-05'
 *   }
 * });
 * ```
 *
 * @example DELETE request
 * ```typescript
 * // Delete availability block - Path param is type checked
 * await api.delete('/gp-bookings/v1/admin/availability/blocks/:id', {
 *   params: { id: 123 }
 * });
 * ```
 */

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type EndpointConfig<
	TPath extends keyof APIEndpoints,
	TMethod extends Method,
> = TPath extends keyof APIEndpoints
	? TMethod extends keyof APIEndpoints[TPath]
		? APIEndpoints[TPath][TMethod]
		: never
	: never;

type ApiFetchOptions = Parameters<typeof apiFetch>[0];

type ExtractParams<T> = T extends { params: infer P } ? P : never;
type ExtractBody<T> = T extends { body: infer B } ? B : never;
type ExtractResponse<T> = T extends { response: infer R } ? R : never;

type RequestConfig<TBody = unknown, TParams = unknown> = {
	data?: TBody;
	params?: TParams;
	options?: Partial<ApiFetchOptions>;
};

function createTypedApiFetch() {
	// Initialize cache middleware
	apiFetch.use(createCacheMiddleware());

	function buildPath<TPath extends keyof APIEndpoints>(
		path: TPath,
		params?: Record<string, unknown>
	): string {
		if (!params) {
			return path;
		}

		let finalPath = path as string;
		const queryParams: Record<string, unknown> = {};

		// Handle path params vs query params
		for (const [key, value] of Object.entries(params)) {
			if (finalPath.includes(`:${key}`)) {
				finalPath = finalPath.replace(`:${key}`, String(value));
			} else {
				queryParams[key] = value;
			}
		}

		// Add query params if any exist
		const query = new URLSearchParams();
		for (const [key, value] of Object.entries(queryParams)) {
			if (Array.isArray(value)) {
				value.forEach((v) => query.append(`${key}[]`, String(v)));
			} else if (value !== undefined) {
				query.append(key, String(value));
			}
		}

		const queryString = query.toString();
		return queryString ? `${finalPath}?${queryString}` : finalPath;
	}

	return {
		get: <TPath extends keyof APIEndpoints>(
			path: TPath,
			config?: RequestConfig<
				never,
				ExtractParams<EndpointConfig<TPath, 'GET'>>
			>
		): Promise<ExtractResponse<EndpointConfig<TPath, 'GET'>>> => {
			const baseOptions: ApiFetchOptions = {
				method: 'GET',
				path: buildPath(
					path,
					config?.params as Record<string, unknown>
				),
			};

			return apiFetch({
				...baseOptions,
				...config?.options,
			});
		},

		post: <TPath extends keyof APIEndpoints>(
			path: TPath,
			config: RequestConfig<
				ExtractBody<EndpointConfig<TPath, 'POST'>>,
				ExtractParams<EndpointConfig<TPath, 'POST'>>
			>
		): Promise<ExtractResponse<EndpointConfig<TPath, 'POST'>>> => {
			const baseOptions: ApiFetchOptions = {
				method: 'POST',
				path: buildPath(path, config.params as Record<string, unknown>),
				data: config.data,
			};

			return apiFetch({
				...baseOptions,
				...config.options,
			});
		},

		put: <TPath extends keyof APIEndpoints>(
			path: TPath,
			config: RequestConfig<
				ExtractBody<EndpointConfig<TPath, 'PUT'>>,
				ExtractParams<EndpointConfig<TPath, 'PUT'>>
			>
		): Promise<ExtractResponse<EndpointConfig<TPath, 'PUT'>>> => {
			const baseOptions: ApiFetchOptions = {
				method: 'PUT',
				path: buildPath(path, config.params as Record<string, unknown>),
				data: config.data,
			};

			return apiFetch({
				...baseOptions,
				...config.options,
			});
		},

		patch: <TPath extends keyof APIEndpoints>(
			path: TPath,
			config: RequestConfig<
				ExtractBody<EndpointConfig<TPath, 'PATCH'>>,
				ExtractParams<EndpointConfig<TPath, 'PATCH'>>
			>
		): Promise<ExtractResponse<EndpointConfig<TPath, 'PATCH'>>> => {
			const baseOptions: ApiFetchOptions = {
				method: 'PATCH',
				path: buildPath(path, config.params as Record<string, unknown>),
				data: config.data,
			};

			return apiFetch({
				...baseOptions,
				...config.options,
			});
		},

		delete: <TPath extends keyof APIEndpoints>(
			path: TPath,
			config?: RequestConfig<
				never,
				ExtractParams<EndpointConfig<TPath, 'DELETE'>>
			>
		): Promise<ExtractResponse<EndpointConfig<TPath, 'DELETE'>>> => {
			const baseOptions: ApiFetchOptions = {
				method: 'DELETE',
				path: buildPath(
					path,
					config?.params as Record<string, unknown>
				),
			};

			return apiFetch({
				...baseOptions,
				...config?.options,
			});
		},
	};
}

export const api = createTypedApiFetch();
